const windowAlias = window;
/**
 * Hides an element with a slide effect.
 *
 * @param {HTMLElement} target The element to be hidden.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {boolean} [horizontal=false] If true, the element will be hidden with a horizontal animation.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
const slideHide = (target, callback, horizontal = false, duration = 250) => {
  const property = horizontal ? 'width' : 'height';
  const offsetProperty = horizontal ? 'offsetWidth' : 'offsetHeight';
  const style = target.style;
  // Set up the transition
  style.transitionProperty = `${property}, opacity`;
  style.transitionDuration = duration + 'ms';
  style.boxSizing = 'border-box';
  // Set the initial height/width to the current value
  style[property] = target[offsetProperty] + 'px';
  style.overflow = 'hidden';
  // Set the height/width to 0 to start the animation
  style[property] = 0;
  style.opacity = 0;
  // Remove the transition and other styles when the animation is finished
  windowAlias.setTimeout(() => {
    style.removeProperty('display');
    style.removeProperty(property);
    style.removeProperty('opacity');
    style.removeProperty('overflow');
    style.removeProperty('transition-duration');
    style.removeProperty('transition-property');
    if (typeof callback === 'function') {
      callback();
    }
  }, duration);
};

/**
 * Shows an element with a slide effect.
 *
 * @param {HTMLElement} target The element to be shown.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {boolean} [horizontal=false] If true, the element will be shown with a horizontal animation.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
const slideShow = (target, callback, horizontal = false, duration = 250) => {
  const property = horizontal ? 'width' : 'height';
  const offsetProperty = horizontal ? 'offsetWidth' : 'offsetHeight';

  const style = target.style;
  // Set the initial display property to block if it is not set
  style.removeProperty('display');
  let display = window.getComputedStyle(target).display;

  if (display === 'none') {
    display = 'block';
  }

  // Set the initial opacity to 0
  style.opacity = 0;
  // Set the display property to the value that was set above
  style.display = display;
  // Get the initial offset of the element
  const offset = target[offsetProperty];
  // Set the overflow to hidden to prevent the element from being visible during the animation
  style.overflow = 'hidden';
  // Set the initial height/width to 0 to start the animation
  style[property] = 0;
  // Set the boxSizing to border-box to include the padding and border in the calculation of the height/width
  style.boxSizing = 'border-box';
  // Set the transition properties to animate the height/width and opacity
  style.transitionProperty = `${property}, opacity`;
  style.transitionDuration = duration + 'ms';
  // Set the final height/width to the initial offset
  style[property] = offset + 'px';
  // Remove the opacity property to make the element visible again
  style.removeProperty('opacity');
  // When the animation is finished, remove the transition properties and the overflow property
  windowAlias.setTimeout(() => {
    style.removeProperty(property);
    style.removeProperty('overflow');
    style.removeProperty('transition-duration');
    style.removeProperty('transition-property');
    if (typeof callback === 'function') {
      callback();
    }
  }, duration);
};

/**
 * Hides an element with a vertical slide effect.
 *
 * @param {HTMLElement} target The element to be hidden.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
export const slideUp = (target, callback, duration = 250) => {
  slideHide(target, callback, false, duration);
};

/**
 * Shows an element with a vertical slide effect.
 *
 * @param {HTMLElement} target The element to be shown.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
export const slideDown = (target, callback, duration = 250) => {
  slideShow(target, callback, false, duration);
};

/**
 * Hides an element with a horizontal slide effect.
 *
 * @param {HTMLElement} target The element to be hidden.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
export const slideOut = (target, callback, duration = 250) => {
  slideHide(target, callback, true, duration);
};

/**
 * Shows an element with a horizontal slide effect.
 *
 * @param {HTMLElement} target The element to be shown.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
export const slideIn = (target, callback, duration = 250) => {
  slideShow(target, callback, true, duration);
};

/**
 * Toggles the visibility of an element with a vertical slide effect.
 *
 * @param {HTMLElement} target The element to be toggled.
 * @param {Function} [callback] A callback that will be called when the animation is finished.
 * @param {number} [duration=250] The duration of the animation in milliseconds.
 */
export const slideToggle = (target, callback, duration = 250) => {
  // Check the current display property of the target element
  const display = windowAlias.getComputedStyle(target).display;
  // If the element is currently hidden, show it with a slide down effect
  if (display === 'none') {
    return slideDown(target, callback, duration);
    // If the element is currently visible, hide it with a slide up effect
  } else {
    return slideUp(target, callback, duration);
  }
};
